import { useQuery } from "@apollo/client";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import Box from "@mui/material/Box";
import { compact } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import tinycolor from "tinycolor2";
import { getFragmentData, graphql } from "../../../../api/__generated__";
import useSelectTranslation from "../../../../api/languages/useSelectTranslation";
import { OrganizationFragment } from "../../../../api/organizations/fragments";
import {
  BloomUpNamespacesEnum,
  I18Namespaces,
} from "../../../language/I18Namespaces";
import LoadingPage from "../../../layout/LoadingPage";
import { RouteNames } from "../../../routes/routeNames";
import Footer from "../components/Footer";
import Header from "../components/Header";
import NavigationBar from "../components/NavBar";
import Approach from "../components/content/Approach";
import Privacy from "../components/content/Privacy";
import Steps from "../components/content/Steps";

const queryLandingPage = graphql(`
  # import OrganizationApproach from '../../components/content/Approach.tsx/0_document.graphql'
  # import OrganizationHeader from '../../components/Header.tsx/0_document.graphql'
  # import OrganizationNavBar from '../../components/NavBar.tsx/0_document.graphql'
  # import Organization from '../../../../../api/organizations/fragments.ts/0_document.graphql'
  query OrganisationLandingPage($emailOrCode: String!) {
    organizationByEmailOrCode(emailOrCode: $emailOrCode) {
      ...Organization
      ...OrganizationApproach
      ...OrganizationHeader
      ...OrganizationNavBar
    }
  }
`);

const useOrganizationForLandingPage = ({
  emailOrCode,
}: {
  emailOrCode?: string | null;
}) => {
  const navigate = useNavigate();

  const { data } = useQuery(queryLandingPage, {
    onError: () => {
      navigate("/employee/verify", { replace: true });
    },
    variables: {
      // variables cannot be null, so are replaced with empty strings that will fail to fetch
      emailOrCode: emailOrCode ?? "",
    },
  });

  return data?.organizationByEmailOrCode;
};

const OrganizationLandingPage = () => {
  const [searchParams] = useSearchParams();
  const emailOrCode = searchParams.get("email-or-code");
  const { t } = useTranslation<I18Namespaces>([
    BloomUpNamespacesEnum.Organization,
  ]);

  const selectTranslation = useSelectTranslation();
  const { trackPageView } = useMatomo();
  const organization = useOrganizationForLandingPage({
    emailOrCode,
  });

  const organizationData = getFragmentData(OrganizationFragment, organization);

  useEffect(() => {
    if (organizationData)
      trackPageView({
        customDimensions: [
          {
            id: 2,
            value: organizationData.id.toString(),
          },
        ],
        documentTitle: "Organization landing index",
      });
  }, [organizationData, selectTranslation, trackPageView]);

  if (!organization || !organizationData) return <LoadingPage full={false} />;

  const {
    coachingEnabled,
    color,
    mindlabEnabled,
    name,
    ssoEnabled,
    videoEnabled,
  } = organizationData;

  const nameForTranslation = selectTranslation(name);
  const organizationName = !nameForTranslation
    ? t("organization:landing.noOrganizationName")
    : nameForTranslation;

  const baseUrl = "/employee";
  const organizationColor = color ?? "#15262A";

  const hslColor = tinycolor(organizationColor).toHsl();

  const backgroundColor = tinycolor(
    hslColor.l < 85 ? { ...hslColor, l: 96 } : hslColor,
  ).toHexString();

  return (
    <Box
      sx={(theme) => ({
        "& > *:nth-of-type(n+3)": {
          marginTop: "6rem",
        },
        [theme.breakpoints.down(768)]: {
          "& > *:nth-of-type(n+3)": {
            marginTop: "2rem",
          },
        },
        [theme.breakpoints.down(440)]: {
          "& > *:nth-of-type(n+3)": {
            marginTop: "0.5rem",
          },
        },
        alignItems: "center",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
      })}
    >
      <NavigationBar
        backgroundColor={organizationColor}
        baseUrl={baseUrl}
        navigation={compact([
          mindlabEnabled && {
            label: t("organization:landing.navigation.selfcare"),
            path: `${baseUrl}${
              RouteNames.NewB2BLandingPages.selfCare.path
            }/?${searchParams.toString()}`,
          },
          videoEnabled && {
            label: t("organization:landing.navigation.video"),
            path: `/employee/video/?${searchParams.toString()}`,
          },
        ])}
        organization={organization}
      />
      <Header backgroundColor={backgroundColor} organization={organization} />
      <Approach backgroundColor={backgroundColor} organization={organization} />
      <Privacy
        backgroundColor={backgroundColor}
        organizationName={organizationName}
      />
      <Steps
        mainText={t("organization:landing.represent.text")}
        mainTitle={t("organization:landing.represent.title")}
        steps={[
          {
            imageUrl:
              "https://public-assets.bloomup.org/bloomup-app/organization-landing/Plant+Calendar.svg",
            text: t("organization:landing.represent.steps.1.text"),
            title: t("organization:landing.represent.steps.1.title"),
          },
          {
            imageUrl:
              "https://public-assets.bloomup.org/bloomup-app/organization-landing/Plant+Hurdle+Running.svg",
            text: t("organization:landing.represent.steps.2.text"),
            title: t("organization:landing.represent.steps.2.title"),
          },
          {
            imageUrl:
              "https://public-assets.bloomup.org/bloomup-app/organization-landing/Plant+Mirror.svg",
            text: t("organization:landing.represent.steps.3.text"),
            title: t("organization:landing.represent.steps.3.title"),
          },
          {
            imageUrl:
              "https://public-assets.bloomup.org/bloomup-app/organization-landing/Plant+Sparks.svg",
            text: t(
              "organization:landing.represent.steps.4.text",
              "Onze wetenschappelijke methodes zijn uitgewerkt om blijvende veranderingen teweeg te brengen. Zo bereik je wijzigingen in je gedrag en denken die levenslang voordelen meedragen.",
            ),
            title: t(
              "organization:landing.represent.steps.4.title",
              "Verandering die blijft",
            ),
          },
        ]}
      />
      <Footer
        baseUrl={baseUrl}
        coachingEnabled={coachingEnabled}
        mindlabBusinessId={organizationData.mindlabBusinessId || undefined}
        mindlabEnabled={mindlabEnabled}
        ssoEnabled={ssoEnabled}
        videoEnabled={videoEnabled}
      />
    </Box>
  );
};

export default OrganizationLandingPage;
